import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const SERVICES = [
  "Interior lighting upgrades",
  "Custom trim & dash work",
  "Head unit upgrades",
  "Backup cameras & tech",
  "Custom fabrication",
  "LED lighting packages",
  "Wraps & window tint",
  "Rims & stance fitment",
];

const GALLERY = [
  {
    src: "/assets/gallery-corvette.jpg",
    tag: "Full Build",
    caption: "Night-build Corvette — custom trunk amp rack & enclosure",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    src: "/assets/gallery-starlight.jpg",
    tag: "Lighting",
    caption: "Starlight headliner — hundreds of fiber-optic points",
    span: "lg:row-span-2",
  },
  {
    src: "/assets/gallery-carplay-1.jpg",
    tag: "Tech",
    caption: "Infiniti G — Apple CarPlay head unit upgrade",
    span: "",
  },
  {
    src: "/assets/gallery-underglow.jpg",
    tag: "Lighting",
    caption: "Classic Cadillac — full LED underglow package",
    span: "",
  },
  {
    src: "/assets/gallery-amprack.jpg",
    tag: "Fabrication",
    caption: "Trunk amp rack — clean power, hidden wiring",
    span: "lg:col-span-2",
  },
  {
    src: "/assets/gallery-carplay-2.jpg",
    tag: "Tech",
    caption: "Mercedes CLA — CarPlay integration, factory look",
    span: "",
  },
];

export default function CustomSection() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="custom" className="relative py-24 sm:py-36 bg-[#0D0F14] border-y border-white/5" data-testid="custom-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-end mb-14">
          <Reveal>
            <Eyebrow>Section 02 — Customizations</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              Make It
              <span className="text-outline-volt"> Yours.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base sm:text-lg text-white/70 leading-relaxed">
              This isn't about gimmicks. Clean installs, quality parts, and upgrades that
              actually elevate your vehicle. Have a vision? Bring it. No idea where to
              start? We'll help you design it.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {SERVICES.map((s, i) => (
                <span
                  key={s}
                  data-testid={`custom-service-${i}`}
                  className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-tech text-[11px] uppercase tracking-widest text-white/60 transition-colors duration-300 hover:border-[#FF5500] hover:text-white"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#FF5500]" />
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <div
          id="gallery"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[240px] gap-4"
          data-testid="work-gallery"
        >
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={i * 0.07} className={g.span}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setSelected(i)}
                className="group relative block h-full w-full overflow-hidden rounded-2xl border border-white/10 text-left"
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-tech text-[10px] uppercase tracking-[0.25em] text-[#FF5500] mb-1">
                    {g.tag}
                  </p>
                  <p className="text-sm font-medium text-white/90">{g.caption}</p>
                </div>
                <div className="absolute inset-0 rounded-2xl border border-[#FF5500]/0 transition-colors duration-500 group-hover:border-[#FF5500]/60" />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/92 backdrop-blur-md p-4"
            onClick={() => setSelected(null)}
            data-testid="gallery-lightbox"
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 24 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="lightbox-close"
                onClick={() => setSelected(null)}
                className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white hover:border-[#FF5500] hover:text-[#FF5500] transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <img
                src={GALLERY[selected].src}
                alt={GALLERY[selected].caption}
                className="max-h-[75vh] w-full rounded-xl border border-white/10 object-contain bg-black"
              />
              <figcaption className="mt-4 flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FF5500]" />
                <span className="text-sm text-white/80">{GALLERY[selected].caption}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
